import type { ArtworkRequestListItem } from "@hops/sdk/types";
import { StatusBadge } from "./StatusBadge";

interface RequestListItemProps {
  item: ArtworkRequestListItem;
  isActive?: boolean;
  onClick?: (item: ArtworkRequestListItem) => void;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function isOverdue(neededByDate: string | null | undefined): boolean {
  if (!neededByDate) return false;
  return new Date(neededByDate) < new Date();
}

export function RequestListItem({
  item,
  isActive,
  onClick,
}: RequestListItemProps) {
  const overdue = isOverdue(item.neededByDate);

  return (
    <div
      onClick={() => onClick?.(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.(item);
      }}
      data-testid={`panel-request-${item.requestId ?? item.id}`}
      style={{
        padding: "var(--panel-spacing-sm) var(--panel-spacing-md)",
        borderBottom: "1px solid var(--panel-border)",
        cursor: "pointer",
        background: isActive ? "var(--panel-surface-active)" : "transparent",
      }}
      onMouseEnter={(e) => {
        if (!isActive)
          e.currentTarget.style.background = "var(--panel-surface-hover)";
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.background = "transparent";
      }}
    >
      {/* Top row: request ID + status */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "var(--panel-spacing-xs)",
        }}
      >
        <span
          style={{ fontWeight: 600, fontSize: "var(--panel-font-size-md)" }}
        >
          {item.requestId ?? `#${item.id}`}
        </span>
        <StatusBadge
          statusCode={item.statusCode}
          statusDescription={item.statusDescription}
        />
      </div>

      {/* Middle row: customer + product */}
      <div
        style={{
          fontSize: "var(--panel-font-size-sm)",
          color: "var(--panel-text-muted)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          marginBottom: 2,
        }}
      >
        {item.custOrgName ?? "Unknown customer"}
        {item.productName ? ` · ${item.productName}` : ""}
      </div>

      {/* Bottom row: needed-by + type + artist */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          fontSize: "var(--panel-font-size-xs)",
          color: "var(--panel-text-muted)",
        }}
      >
        {item.neededByDate && (
          <span
            style={{
              color: overdue ? "var(--panel-danger)" : undefined,
              fontWeight: overdue ? 600 : undefined,
            }}
          >
            Due {formatDate(item.neededByDate)}
          </span>
        )}
        {item.requestType && (
          <span style={{ marginLeft: 8 }}>{item.requestType}</span>
        )}
        {item.assignedToFullName && (
          <span style={{ marginLeft: "auto" }}>{item.assignedToFullName}</span>
        )}
      </div>
    </div>
  );
}
